<template>
	<page-content-layout class="fraud-rules" title="Bedrageriregler">
		<button-group-view slot="tools" :actions="categoryActions" v-model="category" />

		<div class="fraud-rules__body">
			<div v-if="loading" class="fraud-rules__state">Indlæser regelkatalog…</div>
			<div v-else-if="error" class="fraud-rules__state fraud-rules__state--error">{{ error }}</div>
			<div v-else>

				<!-- Page header — anchors what you're looking at and how active each layer is -->
				<header class="fraud-rules__header">
					<div class="fraud-rules__lede">
						{{ items.length }} regler overvåger systemet for kreative mønstre.
						Kataloget kommer direkte fra detektorerne — det du ser her, er præcis det engine'n leder efter.
					</div>
					<div class="fraud-rules__totals">
						<div v-for="t in totals" :key="t.key" class="fraud-rules__total" :class="'fraud-rules__total--' + t.key">
							<span class="fraud-rules__total-num">{{ t.open }}</span>
							<span class="fraud-rules__total-lbl">{{ t.label }} åbne</span>
						</div>
					</div>
				</header>

				<section v-for="group in groups" :key="group.key" class="fraud-rules__group">
					<div class="fraud-rules__group-head">
						<h2 class="fraud-rules__group-title">{{ group.label }}</h2>
						<span class="fraud-rules__group-count">{{ group.rules.length }} {{ group.rules.length === 1 ? 'regel' : 'regler' }}</span>
					</div>

					<div class="fraud-rules__grid">
						<article v-for="rule in group.rules" :key="rule.id" class="fraud-rules__card">
							<div class="fraud-rules__card-head">
								<severity-badge-view :severity="rule.severity" />
								<span class="fraud-rules__card-title" :title="rule.display_name">{{ rule.display_name }}</span>
								<span v-if="rule.detector_version > 1" class="fraud-rules__version">v{{ rule.detector_version }}</span>
							</div>
							<p class="fraud-rules__card-desc">{{ rule.description }}</p>
							<footer class="fraud-rules__card-foot">
								<div class="fraud-rules__stat fraud-rules__stat--open" :class="{ 'is-zero': openFor(rule) === 0 }">
									<span class="fraud-rules__stat-num">{{ openFor(rule) }}</span>
									<span class="fraud-rules__stat-lbl">åbne</span>
								</div>
								<div class="fraud-rules__stat fraud-rules__stat--escalated" :class="{ 'is-zero': escalatedFor(rule) === 0 }">
									<span class="fraud-rules__stat-num">{{ escalatedFor(rule) }}</span>
									<span class="fraud-rules__stat-lbl">eskaleret</span>
								</div>
								<div class="fraud-rules__stat fraud-rules__stat--dismissed" :class="{ 'is-zero': dismissedFor(rule) === 0 }">
									<span class="fraud-rules__stat-num">{{ dismissedFor(rule) }}</span>
									<span class="fraud-rules__stat-lbl">afvist</span>
								</div>
							</footer>
						</article>
					</div>
				</section>

				<div v-if="!groups.length" class="fraud-rules__state">Ingen regler i denne kategori.</div>
			</div>
		</div>
	</page-content-layout>
</template>

<script>
import SeverityBadgeView from '../views/SeverityBadgeView'

const CATEGORY_LABELS = {
	leave:     'Fravær',
	hours:     'Timer',
	financial: 'Økonomi'
};

// Display order: leave first (most common signal), financial last.
const CATEGORY_ORDER = ['leave', 'hours', 'financial'];

// Sort order within a category: critical → warn → info. Reviewers scan
// top-down looking for the worst stuff.
const SEVERITY_RANK = { critical: 0, warn: 1, info: 2 };

export default {
	components: { SeverityBadgeView },
	data() {
		return {
			items:    [],
			byRule:   {},   // rule_id → {open, dismissed, escalated}
			loading:  true,
			error:    null,
			category: 'all'
		};
	},
	computed: {
		categoryActions() {
			return [
				{ label: 'Alle',    value: 'all'       },
				{ label: 'Fravær',  value: 'leave'     },
				{ label: 'Timer',   value: 'hours'     },
				{ label: 'Økonomi', value: 'financial' }
			];
		},
		groups() {
			const filtered = this.category === 'all'
				? this.items
				: this.items.filter(r => r.category === this.category);

			const byCat = {};
			for (const r of filtered) {
				if (!byCat[r.category]) byCat[r.category] = [];
				byCat[r.category].push(r);
			}

			// Sort each bucket by severity rank, then by display_name for
			// stable ordering of equal-severity rules.
			for (const k of Object.keys(byCat)) {
				byCat[k].sort((a, b) => {
					const sa = SEVERITY_RANK[a.severity] ?? 99;
					const sb = SEVERITY_RANK[b.severity] ?? 99;
					if (sa !== sb) return sa - sb;
					return a.display_name.localeCompare(b.display_name, 'da');
				});
			}

			return CATEGORY_ORDER
				.filter(k => byCat[k] && byCat[k].length)
				.map(k => ({
					key:   k,
					label: CATEGORY_LABELS[k] || k,
					rules: byCat[k]
				}));
		},
		totals() {
			// Top-of-page summary cards — sum across all rules per severity.
			const tally = { critical: 0, warn: 0, info: 0 };
			for (const r of this.items) {
				const open = this.openFor(r);
				if (open && tally[r.severity] !== undefined) tally[r.severity] += open;
			}
			return [
				{ key: 'critical', label: 'kritiske',    open: tally.critical },
				{ key: 'warn',     label: 'advarsler',   open: tally.warn     },
				{ key: 'info',     label: 'info',        open: tally.info     }
			];
		}
	},
	mounted() {
		this.load();
	},
	methods: {
		openFor(rule)      { return (this.byRule[rule.id] && this.byRule[rule.id].open)      || 0; },
		dismissedFor(rule) { return (this.byRule[rule.id] && this.byRule[rule.id].dismissed) || 0; },
		escalatedFor(rule) { return (this.byRule[rule.id] && this.byRule[rule.id].escalated) || 0; },

		async load() {
			this.loading = true;
			this.error   = null;
			try {
				// Detectors + summary in parallel — page renders once both land.
				const [det, sum] = await Promise.all([
					this.axios.get('/fraud/detectors/'),
					this.axios.get('/fraud/alarms/summary/')
				]);
				this.items  = det.data._items   || [];
				this.byRule = (sum.data && sum.data._byRule) || {};
			} catch (err) {
				console.error('Failed to load fraud rules', err);
				this.error = 'Kunne ikke indlæse regelkataloget.';
				this.items = [];
				this.byRule = {};
			} finally {
				this.loading = false;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/* Page is on a white background (matches the rest of the desktop app —
   side-windows are dark, pages are light). All surface colors in here
   come from the existing palette: #F7F7F7 / #f0f0f0 tonal accents per
   NewTableView; no new colors invented. */
.fraud-rules {
	&__body {
		padding: 24px 30px 40px;
		color: #333;
	}

	&__state {
		padding: 40px 0;
		color: #888;
		font-size: 14px;

		&--error { color: #f7223f; }
	}

	/* ---------- header ---------- */

	&__header {
		display: flex;
		gap: 24px;
		align-items: flex-start;
		justify-content: space-between;
		margin-bottom: 32px;
		padding-bottom: 24px;
		border-bottom: 1px solid #f0f0f0;
	}

	&__lede {
		flex: 0 1 640px;
		font-size: 14px;
		line-height: 1.6;
		color: #666;
	}

	&__totals {
		display: flex;
		gap: 12px;
		margin-left: auto; /* pin to the right edge regardless of lede width */
	}

	&__total {
		min-width: 120px;
		padding: 14px 18px;
		border-radius: 8px;
		display: flex;
		flex-direction: column;
		gap: 4px;
		color: #ffffff;

		&--critical { background: #f7223f; }
		&--warn     { background: #d6a000; }
		&--info     { background: #6c8eb6; }
	}

	&__total-num {
		font-size: 24px;
		font-weight: 600;
		color: #ffffff;
		line-height: 1;
	}

	&__total-lbl {
		font-size: 11px;
		color: rgba(255, 255, 255, 0.85);
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	/* ---------- category groups ---------- */

	&__group {
		margin-bottom: 36px;

		&:last-child { margin-bottom: 0; }
	}

	&__group-head {
		display: flex;
		align-items: baseline;
		gap: 12px;
		margin-bottom: 14px;
	}

	&__group-title {
		font-size: 13px;
		font-weight: 600;
		color: #888;
		text-transform: uppercase;
		letter-spacing: 1.5px;
		margin: 0;
	}

	&__group-count {
		font-size: 11px;
		color: #aaa;
	}

	/* ---------- card grid ---------- */

	&__grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(420px, 1fr));
		gap: 12px;
	}

	&__card {
		background: #ffffff;
		border: 1px solid #e8e8e8;
		border-radius: 8px;
		padding: 16px 18px;
		display: flex;
		flex-direction: column;
		gap: 10px;
		transition: border-color 120ms ease, background 120ms ease, box-shadow 120ms ease;

		&:hover {
			border-color: #d4d4d4;
			background: #fafafa;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
		}
	}

	&__card-head {
		display: flex;
		align-items: center;
		gap: 10px;
		flex-wrap: wrap;
	}

	&__card-title {
		font-size: 16px;
		font-weight: 600;
		color: #222;
		flex: 1;
		min-width: 0;
		line-height: 1.3;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__version {
		font-size: 11px;
		font-weight: 600;
		color: #6c8eb6;
		background: rgba(108, 142, 182, 0.10);
		padding: 2px 7px;
		border-radius: 3px;
	}

	&__card-desc {
		margin: 0;
		font-size: 14px;
		line-height: 1.6;
		color: #555;
		flex: 1; /* absorbs spare height so the footer sits at the card bottom */
	}

	/* Footer = full-width strip pinned to the card bottom. Three equal
	   columns (mini-stats) so cards in a row visually line up regardless
	   of how long each description runs. */
	&__card-foot {
		display: grid;
		grid-template-columns: 1fr 1fr 1fr;
		margin: 6px -18px -16px; /* extend to the card edges, undoing card padding */
		border-top: 1px solid #f0f0f0;
		background: #fafafa;
		border-radius: 0 0 8px 8px;
		overflow: hidden;
	}

	&__stat {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 2px;
		padding: 10px 8px;
		border-left: 1px solid #f0f0f0;

		&:first-child { border-left: none; }
	}

	&__stat-num {
		font-size: 18px;
		font-weight: 600;
		line-height: 1;
	}

	&__stat-lbl {
		font-size: 10px;
		color: #888;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	&__stat--open      .fraud-rules__stat-num { color: #f7223f; }
	&__stat--escalated .fraud-rules__stat-num { color: #d6a000; }
	&__stat--dismissed .fraud-rules__stat-num { color: #555;    }

	&__stat.is-zero {
		.fraud-rules__stat-num { color: #c4c4c4; }
		.fraud-rules__stat-lbl { color: #b8b8b8; }
	}
}
</style>
